// Rating one at a time, the deck (DESIGN.md, Views, "Rating one at a time"; features/interest.md): what
// waits on my rating, an area at a time in the order of the trip's stays, a card at a time. Plain
// functions with type-only imports, so the server-side tests can use them too; Deck.tsx draws it.
import { TIERS, TIERS_LISTED } from "../../src/tiers.ts";
import { waitsOnRating } from "../../src/waits.ts";
import type { Item, Person } from "../../src/web/types.ts";
import { kindOf } from "./format.ts";
import { distanceM, type LngLat, point } from "./geo.ts";
import { listLayout } from "./maplist.ts";
import { ratingWaits } from "./plan.ts";

// The tiers a deck answer gives, No, Maybe, Yes: the buttons left to right and the keys 1 to 3.
export const ANSWERS = TIERS;
export type Answer = (typeof ANSWERS)[number];

// One area of the deck: where we stay, named as the map's list names its groups ("Osaka", the stays in one
// city together; "Near Hotel Granvia Kyoto" for a stay with no city), then Elsewhere; by kind while no stay
// is chosen or booked with a place, as the list does then. `stays` are where the area's stays are, to say
// how far each place is from the nearest.
export interface DeckArea {
  key: string;
  label: string;
  stays: { title: string; at: LngLat }[];
  ids: string[];
}

// One answer, in the order given: a tier, or null for Later (it stays waiting, and comes again next
// time). `change` is the change that saved it, once it's saved, for Undo.
export interface DeckAnswer {
  id: string;
  tier: Answer | null;
  change?: string | null;
}

// A run through the deck, from opening it to closing it: what waited when it opened, by area (what
// comes in while it's open waits for the next run), the answers given, the area being shown, and what was
// taken back (Undo), which shows again whatever the trip as loaded says until it's answered again (the
// trip loads again after the undo lands). Kept with each step it shows on (router.ts), named by `id`.
export interface DeckRun {
  id: string;
  areas: DeckArea[];
  answers: DeckAnswer[];
  at: number;
  back: string[];
}

const SETTLED = new Set(["planned", "booked", "done"]);
const NOBODY: [number, number] = [0, 0];
const collator = new Intl.Collator("en", { sensitivity: "base", numeric: true });

// How much the others want it: their yeses, then how many of them said Yes or Maybe (only people still in
// the trip; a No doesn't count).
function othersWant(item: Item, members: readonly Person[], you: string): [number, number] {
  const ids = new Set(members.filter((m) => m.id !== you).map((m) => m.id));
  const said = item.interest.filter((x) => ids.has(x.person) && x.tier && x.tier !== "no");
  return [said.filter((x) => x.tier === "yes").length, said.length];
}

// What waits on my rating, by area, in the order of the trip's stays (the map's list: `listLayout`), then
// Elsewhere (what's far from every stay, and what has no place yet). Within an area, what the others want
// most first (their yeses, then how many want it), then as the map's list orders it (by kind, then name),
// and what has no place yet last, by name.
export function deckAreas(items: readonly Item[], members: readonly Person[], you: string, start: string | null) {
  // Ideas' places and goals: a choice's options are rated where they're compared, under their choice's row
  // on Bookings (plan.ts, `ratingWaits`).
  const waiting = ratingWaits(items, you).ideas;
  const stays = items.filter((i) => i.type === "stay" && SETTLED.has(i.status) && point(i) !== null);
  const placed = waiting.filter((i) => point(i) !== null);
  const layout = listLayout([...stays, ...placed], start);
  // Each waiting item's group and its place in the list's order.
  const where = new Map<string, { group: number; at: number }>();
  layout.order.forEach(({ item, group }, at) => {
    if (waitsOnRating(item, you)) where.set(item.id, { group, at });
  });
  const elsewhere = layout.heads.length - 1;
  const kindGroup = new Map(layout.heads.map((h, n) => [h.key, n]));
  for (const item of waiting) {
    if (where.has(item.id)) continue;
    const group = layout.byStay ? elsewhere : (kindGroup.get(`kind:${kindOf(item)}`) ?? elsewhere);
    where.set(item.id, { group, at: Number.POSITIVE_INFINITY });
  }
  const want = new Map(waiting.map((i) => [i.id, othersWant(i, members, you)]));
  return layout.heads.flatMap((head, group): DeckArea[] => {
    const ids = waiting
      .filter((i) => where.get(i.id)?.group === group)
      .sort((a, b) => {
        const [wa, wb] = [want.get(a.id) ?? NOBODY, want.get(b.id) ?? NOBODY];
        const [pa, pb] = [where.get(a.id)?.at ?? 0, where.get(b.id)?.at ?? 0];
        return (
          wb[0] - wa[0] ||
          wb[1] - wa[1] ||
          (pa === pb ? collator.compare(a.title, b.title) : pa < pb ? -1 : 1) ||
          a.id.localeCompare(b.id)
        );
      })
      .map((i) => i.id);
    if (ids.length === 0) return [];
    const at = head.stays.flatMap((s) => {
      const p = point(s);
      return p ? [{ title: s.title, at: p }] : [];
    });
    return [{ key: head.key, label: head.label, stays: at, ids }];
  });
}

// A new run: what waits now, from its first area.
export function startRun(
  items: readonly Item[],
  members: readonly Person[],
  you: string,
  start: string | null,
  id: string = Math.random().toString(36).slice(2, 10),
): DeckRun {
  return { id, areas: deckAreas(items, members, you, start), answers: [], at: 0, back: [] };
}

// Whether the run is past this one: answered (Later too), or no longer waiting on me (rated somewhere
// else meanwhile, on its card or by my AI, or dropped or deleted); not while it's just been taken back.
export function isDone(run: DeckRun, id: string, byId: ReadonlyMap<string, Item>, you: string): boolean {
  if (run.answers.some((a) => a.id === id)) return true;
  const item = byId.get(id);
  if (!item) return true;
  return !run.back.includes(id) && !waitsOnRating(item, you);
}

const left = (run: DeckRun, area: DeckArea, byId: ReadonlyMap<string, Item>, you: string) =>
  area.ids.filter((id) => !isDone(run, id, byId, you));

// What the deck shows: the card on top (and the next two under it), how far through its area it is, or,
// with nothing left in the area, its summary; with nothing waiting at all, that.
export type DeckShows =
  | { kind: "card"; area: DeckArea; index: number; ids: string[]; done: number; total: number }
  | {
      kind: "summary";
      area: DeckArea;
      index: number;
      total: number;
      next: { area: DeckArea; left: number; index: number } | null;
    }
  | { kind: "nothing" };

export function deckShows(run: DeckRun, byId: ReadonlyMap<string, Item>, you: string): DeckShows {
  const area = run.areas[run.at];
  if (!area) return { kind: "nothing" };
  const rest = left(run, area, byId, you);
  const total = area.ids.length;
  if (rest.length > 0)
    return { kind: "card", area, index: run.at, ids: rest.slice(0, 3), done: total - rest.length, total };
  return { kind: "summary", area, index: run.at, total, next: nextArea(run, byId, you) };
}

// The next area with something left in it, and how much.
export function nextArea(run: DeckRun, byId: ReadonlyMap<string, Item>, you: string) {
  for (let n = run.at + 1; n < run.areas.length; n++) {
    const area = run.areas[n] as DeckArea;
    const rest = left(run, area, byId, you).length;
    if (rest > 0) return { area, left: rest, index: n };
  }
  return null;
}

// The first area with something left, from where the run is: where a run starts, and where a run whose
// areas were all answered elsewhere goes.
export function firstLeft(run: DeckRun, byId: ReadonlyMap<string, Item>, you: string): number {
  for (let n = 0; n < run.areas.length; n++) if (left(run, run.areas[n] as DeckArea, byId, you).length > 0) return n;
  return Math.max(0, run.areas.length - 1);
}

// A flick: faster than FLICK px/ms, having gone at least FLICK_MIN px.
const FLICK = 0.5;
const FLICK_MIN = 32;

// What letting go of a swipe answers, dragged by (dx, dy) px with the card needing `w` sideways or `h` up
// to count sideways, and moving at (vx, vy) px/ms as it's let go: right past `w` is Yes, left past `w` No,
// or the same on a flick; anything less, and up or down (more than sideways), nothing: Maybe
// is a button and a key, never a swipe (the founder's pick, 2026-10-21). Without a speed, where letting go
// would count by distance alone: what the haptic tick marks as it's crossed.
export function swipeAnswer(
  dx: number,
  dy: number,
  limits: { w: number },
  speed = { vx: 0, vy: 0 },
): "yes" | "no" | null {
  const { w } = limits;
  const { vx } = speed;
  if (Math.abs(dy) > Math.abs(dx)) return null;
  if (dx > w || (vx > FLICK && dx > FLICK_MIN)) return "yes";
  if (dx < -w || (vx < -FLICK && dx < -FLICK_MIN)) return "no";
  return null;
}

// An answer given: on to the next card (the area's summary once it's through).
export function answer(run: DeckRun, id: string, tier: Answer | null): DeckRun {
  return { ...run, answers: [...run.answers, { id, tier }], back: run.back.filter((b) => b !== id) };
}

// The change that saved an answer, once it's known.
export function saved(run: DeckRun, id: string, change: string | null): DeckRun {
  return { ...run, answers: run.answers.map((a) => (a.id === id ? { ...a, change } : a)) };
}

// The last answer taken back (Undo), its card on top again in its area: the run as it is then, and the
// answer.
export function takeBack(run: DeckRun): { run: DeckRun; last: DeckAnswer } | null {
  const last = run.answers.at(-1);
  if (!last) return null;
  const at = run.areas.findIndex((a) => a.ids.includes(last.id));
  const back = [...run.back.filter((b) => b !== last.id), last.id];
  return { run: { ...run, answers: run.answers.slice(0, -1), at: at < 0 ? run.at : at, back }, last };
}

// An Undo that didn't go through: the answer stands, the run on past it again.
export function giveBack(run: DeckRun, last: DeckAnswer): DeckRun {
  return {
    ...run,
    answers: [...run.answers.filter((a) => a.id !== last.id), last],
    back: run.back.filter((b) => b !== last.id),
  };
}

// An answer that didn't save: its card comes back on top.
export function unanswer(run: DeckRun, id: string): DeckRun {
  const at = run.areas.findIndex((a) => a.ids.includes(id));
  return { ...run, answers: run.answers.filter((a) => a.id !== id), at: at < 0 ? run.at : at };
}

// The others who said Yes or Maybe to it: when I say Yes or Maybe, "Monalisa wants this too" as it goes.
// Only people still in the trip, in the trip's order.
export function wantItToo(item: Item, members: readonly Person[], you: string): Person[] {
  return members.filter((m) => m.id !== you && item.interest.some((x) => x.person === m.id && wanting(x.tier)));
}

// Said Yes or Maybe: wanting it, as "Monalisa wants this too" and Wanted together count it.
export const wanting = (tier: string | null | undefined) => tier === "yes" || tier === "maybe";

const listNames = (names: string[]) =>
  names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

// "Monalisa wants this too", "Mei and Ben want this too", "Mei, Ben and 2 others want this too".
export function wantsTooText(people: readonly Person[]): string {
  const names = people.map((p) => p.name);
  if (names.length === 1) return `${names[0]} wants this too`;
  if (names.length <= 3) return `${listNames(names)} want this too`;
  return `${names.slice(0, 2).join(", ")} and ${names.length - 2} others want this too`;
}

// How an area went, at its end: what I rated, by tier, from Yes to No (my rating as the trip has
// it now, else as I just gave it, while it saves); how many I left for later; and what we both want: what I
// said Yes or Maybe to that someone else said Yes or Maybe to, with who.
export interface AreaSummary {
  rated: { tier: Answer; n: number }[];
  later: number;
  together: { item: Item; people: Person[] }[];
}

export function areaSummary(
  run: DeckRun,
  area: DeckArea,
  byId: ReadonlyMap<string, Item>,
  members: readonly Person[],
  you: string,
): AreaSummary {
  const counts = new Map<Answer, number>();
  let later = 0;
  const together: AreaSummary["together"] = [];
  for (const id of area.ids) {
    const item = byId.get(id);
    const given = run.answers.find((a) => a.id === id);
    const now = item?.interest.find((x) => x.person === you)?.tier ?? null;
    const tier = (now ?? given?.tier ?? null) as Answer | null;
    if (tier && ANSWERS.includes(tier)) counts.set(tier, (counts.get(tier) ?? 0) + 1);
    else if (given && given.tier === null) later++;
    if (item && wanting(tier)) {
      const people = wantItToo(item, members, you);
      if (people.length > 0) together.push({ item, people });
    }
  }
  const rated = TIERS_LISTED.flatMap((tier) => (counts.get(tier) ? [{ tier, n: counts.get(tier) as number }] : []));
  return { rated, later, together };
}

// The area's stay a place is nearest, and how far: "800 m from Hotel Granvia Kyoto".
export function fromStay(item: Item, area: DeckArea): { title: string; m: number } | null {
  const at = point(item);
  if (!at) return null;
  let near: { title: string; m: number } | null = null;
  for (const stay of area.stays) {
    const m = distanceM(stay.at, at);
    if (!near || m < near.m) near = { title: stay.title, m };
  }
  return near;
}

// Whether something kept in the history (a run, from a step: router.ts) still reads as a run: it may be
// from an older version of Tripmo.
export function isRun(raw: unknown): raw is DeckRun {
  if (typeof raw !== "object" || raw === null) return false;
  const { id, areas, answers, at, back } = raw as Record<string, unknown>;
  const str = (v: unknown) => typeof v === "string";
  return (
    str(id) &&
    Array.isArray(back) &&
    back.every(str) &&
    Array.isArray(areas) &&
    areas.every((a: unknown) => {
      if (typeof a !== "object" || a === null) return false;
      const { key, label, ids, stays } = a as Record<string, unknown>;
      const staysOk =
        Array.isArray(stays) &&
        stays.every(
          (s: unknown) =>
            typeof s === "object" &&
            s !== null &&
            str((s as Record<string, unknown>)["title"]) &&
            Array.isArray((s as Record<string, unknown>)["at"]),
        );
      return str(key) && str(label) && staysOk && Array.isArray(ids) && ids.every(str);
    }) &&
    Array.isArray(answers) &&
    answers.every((a: unknown) => {
      if (typeof a !== "object" || a === null) return false;
      const { id, tier } = a as Record<string, unknown>;
      return str(id) && (tier === null || ANSWERS.includes(tier as Answer));
    }) &&
    typeof at === "number" &&
    Number.isInteger(at)
  );
}
